/**
 * Bind one unmatched name to an account that already exists (f8.3).
 *
 * **One human, one decision, every slot.** The dialog is opened for a
 * `normalized_key`, not for a cell and not for a slot: `Axel Apice` appears in
 * fifteen cells across all five delivery roles and is one question, because one
 * human is one record. The notice states the breadth before the operator
 * commits to it -- the prototype's *"All 4 Project Head assignments carried by
 * this name will be granted to the chosen account"*, generalised over every
 * role the name holds.
 *
 * **Two sources of candidates, and the difference matters.** An `AMBIGUOUS`
 * name already matched more than one account, and those matches arrive on the
 * decision itself: they are the whole content of the control, and no search is
 * offered because the choice is between them. Any other unmatched name is
 * searched for among every assignable account, which is why the search field
 * appears only then.
 *
 * A deactivated account is never a candidate: the server excludes one from
 * `GET /assignable-users` and would refuse it at commit with
 * `USER_NOT_ASSIGNABLE`, so offering it here would be an invitation to fail.
 *
 * The prototype's `radio-card` list, `search-field` and `alert alert-info`,
 * inside f2.2's `Modal` -- the same `modal-scrim` / `modal-head` / `modal-body`
 * / `modal-foot` shape every other dialog in Atlas uses.
 */

import { useMemo, useState } from 'react';
import { Check, Search } from 'lucide-react';

import type { LookupValue, RoleHolderDecision } from '@/api/client';
import { generalMessage } from '@/api/fieldErrors';
import { useAssignableUsers, useResolveRoleHolder } from '@/api/queries';
import { Modal } from '@/routes/reference-data/Modal';

import { fanOutNotice, initialsOf, matchesSearch, roleLabel } from './resolutionView';

/** A candidate, in the one shape both sources reduce to. */
interface Candidate {
  id: string;
  display_name: string;
  email: string;
  status: string;
}

export function AssignExistingDialog({
  batchId,
  decision,
  deliveryRoles,
  onClose,
}: {
  batchId: string;
  decision: RoleHolderDecision;
  /** f2.2's `DELIVERY_ROLE` catalogue, for the chip and notice labels. */
  deliveryRoles: readonly LookupValue[];
  onClose: () => void;
}) {
  // An ambiguous name's candidates are the accounts it already matched, and
  // they are on the decision. Fetching the whole roster for it would offer the
  // operator every account in Atlas as an answer to "which of these two?".
  // `candidates` is optional on the wire -- it defaults to empty for every
  // name that is not ambiguous -- so it is read once here, never re-defaulted.
  const matched = decision.candidates ?? [];
  const ambiguous = matched.length > 0;
  const roster = useAssignableUsers({ enabled: !ambiguous });
  const resolve = useResolveRoleHolder(batchId);

  const [search, setSearch] = useState('');
  const [userId, setUserId] = useState('');

  const candidates: readonly Candidate[] = ambiguous
    ? matched
    : (roster.data?.users ?? []);

  const shown = useMemo(
    () =>
      ambiguous
        ? candidates
        : candidates.filter((found) =>
            matchesSearch(found.display_name, found.email, search),
          ),
    [ambiguous, candidates, search],
  );

  const submit = () => {
    if (!userId) return;
    resolve.mutate(
      { normalized_key: decision.normalized_key, kind: 'ASSIGN_EXISTING', user_id: userId },
      { onSuccess: onClose },
    );
  };

  const raw = decision.raw_names[0] ?? decision.normalized_key;

  return (
    <Modal
      title="Assign existing user"
      subtitle={`Bind “${raw}” to an Atlas account`}
      onClose={onClose}
      onSubmit={submit}
      busy={resolve.isPending}
      submitLabel="Assign user"
      submitIcon={<Check aria-hidden="true" />}
      layout="plain"
      message={generalMessage(resolve.error, 'user_id')}
    >
      {!ambiguous && (
        <div className="field mb-4">
          <label htmlFor="assign-search">Search users</label>
          <div className="search-field">
            <Search aria-hidden="true" />
            <input
              id="assign-search"
              type="search"
              value={search}
              placeholder="Name or e-mail"
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>
      )}

      {ambiguous && (
        <p className="hint mb-4">
          This name already matches {matched.length} accounts. Choose the
          one the workbook meant — creating another is how the duplicate got here, so
          it is not offered.
        </p>
      )}

      {!ambiguous && roster.isPending ? (
        <span className="skeleton-bar group-skeleton-line" aria-busy="true" />
      ) : (
        <div className="stack-sm radio-card-list" role="radiogroup" aria-label="Candidate accounts">
          {shown.map((found) => (
            <label
              key={found.id}
              className={`radio-card${found.id === userId ? ' is-selected' : ''}`}
            >
              <input
                type="radio"
                name="assignee"
                value={found.id}
                checked={found.id === userId}
                onChange={() => setUserId(found.id)}
              />
              <span className="avatar sm" aria-hidden="true">
                {initialsOf(found.display_name)}
              </span>
              <span className="rc-text">
                <span className="rc-title">{found.display_name}</span>
                <span className="rc-desc">
                  {found.email} · {found.status.replace(/_/g, ' ')}
                </span>
              </span>
            </label>
          ))}
          {shown.length === 0 && (
            <p className="hint">
              No account matches that search. Close this and choose{' '}
              <strong>Create new</strong> if this person has no Atlas account yet.
            </p>
          )}
        </div>
      )}

      <div className="alert alert-info mt-4">
        <div className="a-body">
          {fanOutNotice(decision, deliveryRoles)} They occupy{' '}
          {decision.roles
            .map(
              (occupancy) =>
                `${roleLabel(occupancy.delivery_role_code, deliveryRoles)} ×${occupancy.slot_count}`,
            )
            .join(', ')}
          .
        </div>
      </div>
    </Modal>
  );
}
